import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';

import {
  GeocodeQuery,
  OrderCancellationPolicyGroupDocument,
  RateGroupKindEnum,
} from '@graphql/platform';

import {
  challengerPromoSummaryLabel,
  couponDiscountAmount,
  isStorageCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { useProtectionPlanInfo } from '@root/components/checkout/hooks/use_protection_plan_info';
import { resolvedTaxPercent } from '@root/components/checkout/hooks/use_storage_tax_rate';
import { resolveAppointmentDateTime } from '@root/components/checkout/utilities/appointment_date';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';
import { Commitment } from '@root/resources/types/commitment';
import { PlanName } from '@root/resources/types/plan_name';

import { formatCurrency } from '@utils/formatters';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { extractStoragePrice } from '@utils/pricing/pricing_set';
import { PROTECTION_PLANS, ProtectionPlanEnum } from '@utils/protection_plan';

import { CancellationPolicyModal } from './cancellation_policy_modal';
import { DisposalSummaryRow } from './disposal_summary_row';
import { useChallengerPricingRows } from './hooks/use_challenger_pricing_rows';
import {
  ChallengerTaxRates,
  challengerTaxZip,
} from './hooks/use_challenger_tax_rates';
import { useCloseMobileDrawerOnOutsideInteraction } from './hooks/use_close_mobile_drawer_on_outside_interaction';
import { useKeyboardInset } from './hooks/use_keyboard_inset';
import { useModalFocus } from './hooks/use_modal_focus';
import { TABLET_UP as DESKTOP, MOBILE } from './media';
import { CHALLENGER as T } from './tokens';

/** Match desktop booking summary (Figma 29:3865). */
const COMMITMENT_LABEL: Record<Commitment, string> = {
  [RateGroupKindEnum.Mover]: 'Month to month',
  [RateGroupKindEnum.Flexer]: '4-month minimum',
  [RateGroupKindEnum.Saver]: '8-month minimum',
};

const Shell = styled.div<{ $keyboardInset: number }>`
  display: none;

  ${MOBILE} {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    flex-direction: column;
    align-items: center;
    background: ${T.color.stickySurface};
    border: 1px solid ${T.color.borderDefault};
    border-bottom: none;
    border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
    padding: ${T.space[24]} ${T.space[16]}
      calc(${T.space[16]} + env(safe-area-inset-bottom, 0));
    font-family: ${T.font.sans};
    box-sizing: border-box;
    transform: ${({ $keyboardInset }) =>
      $keyboardInset ? `translateY(${$keyboardInset}px)` : 'none'};
    pointer-events: ${({ $keyboardInset }) =>
      $keyboardInset ? 'none' : 'auto'};
  }

  ${DESKTOP} {
    display: none;
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 6px;
  border-radius: 999px;
  background: #c8d1d3;
  flex-shrink: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[16]};
  width: 100%;
  box-sizing: border-box;
`;

const Left = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${T.space[8]};
`;

const MonthlyLabel = styled.span`
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textSecondary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[12]};
  flex-shrink: 0;
`;

/** Figma 2291:37208 / 2386:30520 — outlined control on the right of the tray. */
const FeesButton = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: ${T.space[12]} ${T.space[16]};
  border: 1px solid ${T.color.borderDefault};
  border-radius: ${T.radius.md};
  background: transparent;
  cursor: pointer;
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textPrimary};
  white-space: nowrap;
`;

/** Compact Continue next to “View all fees” once the plan form is complete. */
const ContinueButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  height: 48px;
  padding: 0 ${T.space[24]};
  flex-shrink: 0;
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const Backdrop = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  position: fixed;
  inset: 0;
  z-index: 29;
  background: ${T.color.overlay};
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  cursor: pointer;

  ${DESKTOP} {
    display: none;
  }
`;

/** Figma Mobile-08 booking summary sheet (707:20865). */
const FeesSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 31;
  max-height: 85vh;
  overflow: auto;
  background: ${T.color.stickySurface};
  border: 1px solid ${T.color.borderDefault};
  border-bottom: none;
  border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
  padding: ${T.space[8]} ${T.space[16]} ${T.space[24]};
  font-family: ${T.font.sans};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[8]};

  ${DESKTOP} {
    display: none;
  }
`;

const SheetInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
`;

const HeaderBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${T.space[8]};
  padding: ${T.space[12]} ${T.space[12]} ${T.space[16]};
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const FeesTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

/**
 * Figma 2291:36635 icon/close — 24×24 frame, 14×14 vector at (5,5),
 * fill text/content/text-tertiary (#66686E). ::after expands the tap
 * target to 44px without changing the visual box.
 */
const CloseButton = styled.button`
  appearance: none;
  position: relative;
  display: block;
  width: 24px;
  height: 24px;
  padding: 0;
  flex-shrink: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    inset: -10px;
  }

  svg {
    position: absolute;
    inset: 0;
    display: block;
    width: 24px;
    height: 24px;
    max-width: none;
  }
`;

const CostBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 ${T.space[12]};
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${T.space[8]};
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textSecondary};
  padding: 2px 0;
`;

const Total = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

/** Match desktop booking summary CTA note (Funnel-Feedback 1:3421). */
const Note = styled.p`
  margin: 0;
  text-align: center;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
`;

const NoteLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${T.color.linkBrand};
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

const CtaBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[12]};
  width: 100%;
`;

const PromoLine = styled(Line)`
  color: ${T.color.textBrandDark};
`;

const SheetContinue = styled.button<{ disabled?: boolean }>`
  appearance: none;
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

type Props = {
  /** All storage-plan page-1 fields captured — show Continue in the tray. */
  planComplete: boolean;
  onContinue: () => void | Promise<void>;
  /** Live slider plan before flow state commits on pointer release. */
  displayPlanSize?: PlanName;
  taxRates: ChallengerTaxRates;
  geocode?: GeocodeQuery['geocode'];
  continueDisabled?: boolean;
};

export const MobileStickyBar: React.FC<Props> = ({
  planComplete,
  onContinue,
  displayPlanSize,
  taxRates,
  geocode,
  continueDisabled = false,
}) => {
  const [feesOpen, setFeesOpen] = useState(false);
  const feesSheetRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const keyboardInset = useKeyboardInset();
  useModalFocus(feesOpen, feesSheetRef, closeButtonRef, {
    lockBeforePaint: true,
  });
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const {
    pricingSet,
    pricingSummary: pricingSummaryRaw,
    defaultPricingSummary,
    flowState: {
      values: {
        planSize,
        commitment,
        bundleKind,
        disposalSelected,
        dateScheduled,
        datePreferred,
        coupon,
        protectionPlan,
        zip,
        address,
      },
    },
  } = useStorageCheckoutContext();
  const pricingSummary =
    useLastDefined(pricingSummaryRaw) ?? defaultPricingSummary;
  const storageTaxRate = taxRates.storage;
  const protectionTaxRate = taxRates.protection;
  const protection = useProtectionPlanInfo('challenger');

  const effectivePlanSize = displayPlanSize ?? planSize;
  const storagePlanAmount = useMemo(() => {
    if (commitment && effectivePlanSize && pricingSet) {
      return extractStoragePrice(commitment, effectivePlanSize, pricingSet);
    }
    return pricingSummary?.monthlyAmount;
  }, [
    commitment,
    effectivePlanSize,
    pricingSet,
    pricingSummary?.monthlyAmount,
  ]);

  const protectionPlanAmount = protection.amount;
  const taxZip = Boolean(challengerTaxZip(zip, address));
  const storageTaxPercent = resolvedTaxPercent(storageTaxRate, taxZip);
  const protectionTaxPercent = resolvedTaxPercent(protectionTaxRate, taxZip);
  const taxesReady = storageTaxPercent != null && protectionTaxPercent != null;
  const summaryLoaded = storagePlanAmount != null && taxesReady;

  const { data: cancellationPolicyData } = useQuery(
    OrderCancellationPolicyGroupDocument,
    {
      variables: {
        visitorToken: WT_VISITOR_TOKEN,
        orderScheduledTime: dateScheduled?.fromTime,
      },
      skip: !geocode || geocode?.region?.name === 'Los Angeles',
    },
  );
  const cancelBy = cancellationPolicyData?.orderCancellationPolicyGroup
    ?.onboardingCancellationPolicy?.latestFreeCancellationDate
    ? DateTime.fromISO(
        cancellationPolicyData.orderCancellationPolicyGroup
          .onboardingCancellationPolicy.latestFreeCancellationDate,
      )
    : resolveAppointmentDateTime({ dateScheduled, datePreferred }).minus({
        days: 2,
      });

  const storagePlanTax =
    storagePlanAmount != null && taxesReady
      ? storagePlanAmount * (storageTaxPercent / 100)
      : undefined;
  const protectionPlanTax = taxesReady
    ? protectionPlanAmount * (protectionTaxPercent / 100)
    : undefined;
  const tax =
    storagePlanTax != null && protectionPlanTax != null
      ? storagePlanTax + protectionPlanTax
      : undefined;
  const monthlyBaseAmount =
    storagePlanAmount != null
      ? storagePlanAmount + protectionPlanAmount
      : undefined;
  const fullMonthlyTotal =
    storagePlanAmount != null && tax != null
      ? storagePlanAmount + protectionPlanAmount + tax
      : undefined;

  const {
    appointmentPromoDiscount,
    disposalAmount,
    gasFeeAmount,
    packingLabel,
    packingPriceDisplay,
    pickupTotalDisplay,
    scheduledArrivalAmount,
    showPackingLine,
    showPickupFees: showPickup,
  } = useChallengerPricingRows({
    bundleKind,
    coupon,
    dateScheduled,
    disposalSelected,
  });

  const storagePromoDiscountRaw =
    coupon && isStorageCoupon(coupon) && monthlyBaseAmount != null
      ? couponDiscountAmount(coupon, monthlyBaseAmount)
      : 0;
  const storagePromoDiscount =
    monthlyBaseAmount != null
      ? Math.min(storagePromoDiscountRaw, monthlyBaseAmount)
      : storagePromoDiscountRaw;

  const monthlyTotal =
    fullMonthlyTotal != null
      ? Math.max(0, fullMonthlyTotal - storagePromoDiscount)
      : undefined;

  const protectionCoverage =
    PROTECTION_PLANS[protectionPlan ?? ProtectionPlanEnum.MINIMAL_COVERAGE]
      .coverage;

  const handleContinue = () => {
    setFeesOpen(false);
    void onContinue();
  };

  useEffect(() => {
    if (!feesOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFeesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [feesOpen]);

  useCloseMobileDrawerOnOutsideInteraction(feesOpen, feesSheetRef, () =>
    setFeesOpen(false),
  );

  return (
    <>
      {feesOpen && (
        <>
          <Backdrop
            type="button"
            aria-label="Close fees"
            onClick={() => setFeesOpen(false)}
          />
          <FeesSheet
            ref={feesSheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Booking summary"
          >
            <Handle aria-hidden />
            <SheetInner>
              <HeaderBlock>
                <FeesTitle>Booking summary</FeesTitle>
                <CloseButton
                  ref={closeButtonRef}
                  type="button"
                  aria-label="Close booking summary"
                  onClick={() => setFeesOpen(false)}
                >
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden
                  >
                    <path
                      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                      fill={T.color.textTertiary}
                    />
                  </svg>
                </CloseButton>
              </HeaderBlock>
              <CostBlock>
                <SectionLabel>Monthly storage plan</SectionLabel>
                {effectivePlanSize ? (
                  <Line>
                    <span>
                      {commitment
                        ? COMMITMENT_LABEL[commitment]
                        : 'Storage plan'}
                    </span>
                    <span>
                      {summaryLoaded ? formatCurrency(storagePlanAmount) : '—'}
                    </span>
                  </Line>
                ) : (
                  <Line>
                    <span>Storage plan</span>
                    <span>—</span>
                  </Line>
                )}
                {protectionPlanAmount > 0 && protectionCoverage && (
                  <Line>
                    <span>{protectionCoverage} of protection</span>
                    <span>{formatCurrency(protectionPlanAmount)}</span>
                  </Line>
                )}
                {coupon && storagePromoDiscount > 0 && (
                  <PromoLine>
                    <span>{challengerPromoSummaryLabel(coupon)}</span>
                    <span>-{formatCurrency(storagePromoDiscount)}</span>
                  </PromoLine>
                )}
                <Line>
                  <span>Tax</span>
                  <span>
                    {summaryLoaded && tax != null ? formatCurrency(tax) : '—'}
                  </span>
                </Line>
                <Total>
                  {summaryLoaded && monthlyTotal != null
                    ? formatCurrency(monthlyTotal)
                    : '—'}
                </Total>
              </CostBlock>
              {showPickup && (
                <CostBlock>
                  <SectionLabel>Your initial pickup includes</SectionLabel>
                  {showPackingLine && packingLabel && (
                    <Line>
                      <span>{packingLabel}</span>
                      <span>{packingPriceDisplay}</span>
                    </Line>
                  )}
                  {scheduledArrivalAmount > 0 && (
                    <Line>
                      <span>Scheduled arrival</span>
                      <span>{formatCurrency(scheduledArrivalAmount)}</span>
                    </Line>
                  )}
                  {disposalSelected && (
                    <DisposalSummaryRow amount={disposalAmount} />
                  )}
                  {gasFeeAmount > 0 && (
                    <Line>
                      <span>Gas fee</span>
                      <span>{formatCurrency(gasFeeAmount)}</span>
                    </Line>
                  )}
                  {coupon && appointmentPromoDiscount > 0 && (
                    <PromoLine>
                      <span>{challengerPromoSummaryLabel(coupon)}</span>
                      <span>-{formatCurrency(appointmentPromoDiscount)}</span>
                    </PromoLine>
                  )}
                  <Total>{pickupTotalDisplay}</Total>
                </CostBlock>
              )}
              <CtaBlock>
                <SheetContinue
                  type="button"
                  disabled={continueDisabled}
                  onClick={handleContinue}
                >
                  Continue
                </SheetContinue>
                <Note>
                  You will be charged upon completion of your pickup. You have
                  until {cancelBy.toFormat('MMMM d')} to cancel. See
                  Clutter&apos;s{' '}
                  <NoteLink
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowCancellationModal(true);
                    }}
                  >
                    cancellation policy
                  </NoteLink>{' '}
                  for more details.
                </Note>
              </CtaBlock>
            </SheetInner>
          </FeesSheet>
        </>
      )}
      {!feesOpen && (
        <Shell
          $keyboardInset={keyboardInset}
          aria-hidden={keyboardInset ? true : undefined}
        >
          <Row>
            <Left>
              <MonthlyLabel>Monthly total</MonthlyLabel>
              <Price>
                {summaryLoaded && monthlyTotal != null
                  ? formatCurrency(monthlyTotal)
                  : '—'}
              </Price>
            </Left>
            <Actions>
              <FeesButton
                type="button"
                aria-haspopup="dialog"
                onClick={() => setFeesOpen(true)}
              >
                View all fees
              </FeesButton>
              {planComplete && (
                <ContinueButton
                  type="button"
                  disabled={continueDisabled}
                  onClick={() => {
                    void onContinue();
                  }}
                >
                  Continue
                </ContinueButton>
              )}
            </Actions>
          </Row>
        </Shell>
      )}
      <CancellationPolicyModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
      />
    </>
  );
};
